import { useAuthStore } from '../store/auth'

const API_BASE = '/internal/api/v1'

/**
 * Build the Authorization header from the current session token, but only
 * if the caller hasn't already supplied one (don't stomp explicit overrides).
 */
function authHeader(callerHeaders: Record<string, string>): Record<string, string> {
  if ('Authorization' in callerHeaders) return {}
  const token = useAuthStore.getState().token
  if (!token) return {}
  return { Authorization: `Bearer ${token}` }
}

/**
 * Handle a 401 response: clear local session and redirect to /login.
 * Skip redirect when the current page is already /login to avoid loops.
 * Returns the parsed error body so callers can still throw it.
 */
async function handle401(res: Response, skipRedirect: boolean): Promise<unknown> {
  const err = await res.json().catch(() => ({
    error: { code: 'unauthorized', message: res.statusText, details: {} },
  }))
  if (!skipRedirect && window.location.pathname !== '/login') {
    useAuthStore.getState().clear()
    window.location.assign('/login')
  }
  return err
}

/** Like apiFetch but returns null on 404 instead of throwing. */
export async function apiFetchAllow404<T>(
  path: string,
  options: RequestInit & { idempotencyKey?: string; skipAuthRedirect?: boolean } = {}
): Promise<T | null> {
  const { idempotencyKey, skipAuthRedirect = false, ...fetchOptions } = options

  const hasBody = fetchOptions.body !== undefined && fetchOptions.body !== null
  const callerHeaders = (options.headers as Record<string, string>) ?? {}

  const headers: Record<string, string> = {
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    ...authHeader(callerHeaders),
    ...callerHeaders,
  }

  const res = await fetch(`${API_BASE}${path}`, { ...fetchOptions, headers })

  if (res.status === 401) {
    const err = await handle401(res, skipAuthRedirect)
    throw err
  }

  if (res.status === 404) {
    return null
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({
      error: { code: 'unknown', message: res.statusText, details: {} },
    }))
    throw err
  }

  if (res.status === 204) {
    return undefined as T
  }

  return res.json()
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit & { idempotencyKey?: string; skipAuthRedirect?: boolean } = {}
): Promise<T> {
  const { idempotencyKey, skipAuthRedirect = false, ...fetchOptions } = options

  const hasBody = fetchOptions.body !== undefined && fetchOptions.body !== null
  const callerHeaders = (options.headers as Record<string, string>) ?? {}

  const headers: Record<string, string> = {
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    ...authHeader(callerHeaders),
    ...callerHeaders,
  }

  const res = await fetch(`${API_BASE}${path}`, { ...fetchOptions, headers })

  if (res.status === 401) {
    const err = await handle401(res, skipAuthRedirect)
    throw err
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({
      error: { code: 'unknown', message: res.statusText, details: {} },
    }))
    throw err
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T
  }

  return res.json()
}

// Build query string from params object, omitting undefined/null values
export function buildQueryString(params: Record<string, string | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  ) as [string, string][]

  if (entries.length === 0) return ''
  return '?' + new URLSearchParams(entries).toString()
}
